import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import type { StripeConnectInstance } from "@stripe/connect-js";
import {
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
} from "@stripe/react-connect-js";
import { AlertTriangle, Check, CreditCard, ExternalLink, Loader2, Lock, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { apiErrorMessage } from "@/lib/api-error";
import { EmptyState, ErrorState, Eyebrow, LoadingState } from "@/components/PageUi";
import { formatDate } from "@/lib/format";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RedirectOnboardingDialog } from "@/components/RedirectOnboardingDialog";
import { useAuth, type ClientType } from "@/auth/AuthContext";
import { authFetch } from "@/api/client";
import {
  usePaymentAccountStatus,
  type PaymentAccountSnapshot,
} from "@/hooks/usePaymentAccountStatus";

const PANEL = "rounded-xl bg-card/60 shadow-none backdrop-blur-sm";

const STRIPE_PUBLISHABLE_KEY_LIVE = process.env.STRIPE_PUBLISHABLE_KEY_LIVE ?? "";
const STRIPE_PUBLISHABLE_KEY_SANDBOX = process.env.STRIPE_PUBLISHABLE_KEY_SANDBOX ?? "";

function pickStripeKey(clientType: ClientType): string {
  // Strict: defaulting an unknown type to a key masked org-switch races that leaked the previous org's Stripe key.
  if (clientType === "Live") return STRIPE_PUBLISHABLE_KEY_LIVE;
  if (clientType === "Sandbox") return STRIPE_PUBLISHABLE_KEY_SANDBOX;
  throw new Error(`Unrecognized client type: ${String(clientType)}`);
}

// Stripe's iframe can't see the page behind it, so translucent tokens are composited over the visible surface
// and handed over as opaque hex.
type RGBA = { r: number; g: number; b: number; a: number };

function parseOklch(value: string): RGBA | null {
  const m = value
    .trim()
    .match(
      /^oklch\(\s*([\d.]+%?)\s+([\d.]+%?)\s+([\d.]+)\s*(?:\/\s*([\d.]+%?))?\s*\)$/,
    );
  if (!m) return null;
  const num = (s: string) => (s.endsWith("%") ? parseFloat(s) / 100 : parseFloat(s));
  const L = num(m[1]!);
  const C = num(m[2]!);
  const H = parseFloat(m[3]!);
  const A = m[4] ? num(m[4]) : 1;
  const hr = (H * Math.PI) / 180;
  const a = C * Math.cos(hr);
  const b = C * Math.sin(hr);
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;
  const lc = l_ ** 3;
  const mc = m_ ** 3;
  const sc = s_ ** 3;
  const r = 4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc;
  const g = -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc;
  const bl = -0.0041960863 * lc - 0.7034186147 * mc + 1.7076147010 * sc;
  const enc = (c: number) => {
    const clamped = Math.max(0, c);
    const v =
      clamped <= 0.0031308
        ? 12.92 * clamped
        : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, v * 255));
  };
  return { r: enc(r), g: enc(g), b: enc(bl), a: A };
}

function parseHex(value: string): RGBA | null {
  const m = value.trim().match(/^#([0-9a-f]{3,8})$/i);
  if (!m) return null;
  const hx = m[1]!;
  const expand = (s: string) => parseInt(s.length === 1 ? s + s : s, 16);
  if (hx.length === 3 || hx.length === 4) {
    return {
      r: expand(hx[0]!),
      g: expand(hx[1]!),
      b: expand(hx[2]!),
      a: hx.length === 4 ? expand(hx[3]!) / 255 : 1,
    };
  }
  if (hx.length === 6 || hx.length === 8) {
    return {
      r: expand(hx.slice(0, 2)),
      g: expand(hx.slice(2, 4)),
      b: expand(hx.slice(4, 6)),
      a: hx.length === 8 ? expand(hx.slice(6, 8)) / 255 : 1,
    };
  }
  return null;
}

function parseRgb(value: string): RGBA | null {
  const m = value
    .trim()
    .match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i);
  if (!m) return null;
  return { r: +m[1]!, g: +m[2]!, b: +m[3]!, a: m[4] ? +m[4]! : 1 };
}

function parseColor(value: string): RGBA | null {
  return parseOklch(value) ?? parseHex(value) ?? parseRgb(value);
}

function composite(fg: RGBA, bg: RGBA): RGBA {
  const a = fg.a + bg.a * (1 - fg.a);
  if (a === 0) return { r: 0, g: 0, b: 0, a: 0 };
  const mix = (f: number, b: number) => (f * fg.a + b * bg.a * (1 - fg.a)) / a;
  return { r: mix(fg.r, bg.r), g: mix(fg.g, bg.g), b: mix(fg.b, bg.b), a };
}

function toHex({ r, g, b }: RGBA): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function buildStripeAppearance() {
  const root = document.documentElement;
  const raw = (token: string) => getComputedStyle(root).getPropertyValue(token).trim();

  const OPAQUE_WHITE: RGBA = { r: 255, g: 255, b: 255, a: 1 };
  const pageBg = parseColor(raw("--background")) ?? OPAQUE_WHITE;
  const resolve = (token: string, base: RGBA): string => {
    const c = parseColor(raw(token));
    if (!c) return "#000000";
    return toHex(c.a >= 1 ? c : composite(c, base));
  };

  // The Stripe embed sits inside a shadcn Card, so the visible surface behind
  // the iframe is --card composited over the page background.
  const cardParsed = parseColor(raw("--card")) ?? OPAQUE_WHITE;
  const surface: RGBA = cardParsed.a >= 1 ? cardParsed : composite(cardParsed, pageBg);

  const primary = resolve("--primary", pageBg);
  const primaryForeground = resolve("--primary-foreground", pageBg);
  const colorBackground = toHex(surface);
  const foreground = resolve("--foreground", surface);
  const mutedForeground = resolve("--muted-foreground", surface);
  const destructive = resolve("--destructive", surface);
  const border = resolve("--border", surface);
  const accent = resolve("--accent", pageBg);

  const fontFamily = getComputedStyle(document.body).fontFamily;

  return {
    variables: {
      fontFamily,
      colorPrimary: primary,
      colorBackground,
      colorText: foreground,
      colorSecondaryText: mutedForeground,
      colorBorder: border,
      colorDanger: destructive,
      formBackgroundColor: colorBackground,
      formAccentColor: primary,
      formHighlightColorBorder: primary,
      offsetBackgroundColor: accent,
      buttonPrimaryColorBackground: primary,
      buttonPrimaryColorBorder: primary,
      buttonPrimaryColorText: primaryForeground,
      // Stripe's radius validator caps at 24px and rejects rem units.
      borderRadius: "10px",
      buttonBorderRadius: "8px",
      formBorderRadius: "8px",
      // Stripe's spacingUnit must be between 8px and 20px.
      spacingUnit: "8px",
    },
  } as const;
}

function useStripeConnect(organisationId: string, clientType: ClientType | null) {
  const [connectInstance, setConnectInstance] = useState<StripeConnectInstance | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const instanceRef = useRef<StripeConnectInstance | null>(null);

  useEffect(() => {
    // Tear down the previous org's instance first, or a live-keyed iframe can stay mounted beside a sandbox one.
    setConnectInstance(null);
    setInitError(null);
    instanceRef.current = null;

    if (!clientType) return;
    let cancelled = false;

    async function fetchClientSecret() {
      const response = await authFetch(
        `/api/proxy/v1/organisations/${organisationId}/payment-account/session`,
        { method: "POST" },
      );
      if (!response.ok) {
        throw new Error(`Session request failed (${response.status})`);
      }
      const body = (await response.json()) as { clientSecret: string };
      return body.clientSecret;
    }

    let publishableKey: string;
    try {
      publishableKey = pickStripeKey(clientType);
    } catch (err) {
      console.error("Failed to pick Stripe key", err);
      setInitError("Payment setup is unavailable right now. Try again later, or contact support if this persists.");
      return;
    }
    if (!publishableKey) {
      const envName = clientType === "Live" ? "STRIPE_PUBLISHABLE_KEY_LIVE" : "STRIPE_PUBLISHABLE_KEY_SANDBOX";
      console.error(`${envName} is not configured; payments onboarding cannot render.`);
      setInitError("Payment setup is unavailable right now. Try again later, or contact support if this persists.");
      return;
    }

    try {
      const instance = loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret,
        appearance: buildStripeAppearance(),
      });
      instanceRef.current = instance;
      if (!cancelled) setConnectInstance(instance);
    } catch (err) {
      if (!cancelled) setInitError(err instanceof Error ? err.message : "Failed to init Stripe");
    }

    // Re-theme the embedded iframe when the app toggles dark mode (shadcn adds
    // `.dark` to <html>), so the Stripe UI tracks the surrounding page.
    const reTheme = () => {
      instanceRef.current?.update({ appearance: buildStripeAppearance() });
    };
    const observer = new MutationObserver(reTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => {
      cancelled = true;
      observer.disconnect();
      instanceRef.current = null;
    };
  }, [organisationId, clientType]);

  return { connectInstance, initError };
}

interface ClientTypeState {
  clientType: ClientType | null;
  isLoading: boolean;
  error: string | null;
}

export function useOrganisationClientType(organisationId: string | null): ClientTypeState {
  // Resolved once and cached in AuthContext, so reading it here makes no network call.
  const { getClientType, clientTypeLoading, clientTypeError } = useAuth();
  const clientType = getClientType(organisationId);
  return {
    clientType,
    isLoading: clientType === null && clientTypeLoading,
    error: clientType === null ? clientTypeError : null,
  };
}

export function PaymentsSettings() {
  const { user, activeOrganisationId } = useAuth();
  const role = useMemo(
    () =>
      user?.organisations.find((o) => o.organisationId === activeOrganisationId)?.role,
    [user, activeOrganisationId],
  );
  const canConfigure = role === "Owner" || role === "Admin";

  if (!canConfigure) {
    return <PermissionGate />;
  }

  if (!activeOrganisationId) {
    return (
      <EmptyState icon={CreditCard} title="No organisation selected">
        Choose an organisation from the switcher in the top bar to set up payments.
      </EmptyState>
    );
  }

  return <PaymentsSettingsAuthorised organisationId={activeOrganisationId} />;
}

function PermissionGate() {
  return (
    <Card className={PANEL}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-muted-foreground" />
          Permissions required
        </CardTitle>
        <CardDescription>
          You don&apos;t have permission to configure payments for this organisation.
          Ask your organisation owner to enable Stripe payments.
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

function PaymentsSettingsAuthorised({ organisationId }: { organisationId: string }) {
  const { data, isLoading, error, forbidden, refresh } =
    usePaymentAccountStatus(organisationId);
  const { clientType, isLoading: clientTypeLoading, error: clientTypeError } =
    useOrganisationClientType(organisationId);

  if (forbidden) return <PermissionGate />;

  // The client type is known before anything Stripe mounts: a demo must never mount them (their session is refused).
  if (isLoading || clientTypeLoading) {
    return <LoadingState label="Loading payment status..." />;
  }

  if (error || clientTypeError) {
    return (
      <ErrorState
        message={`Couldn't load payment status. ${error ?? clientTypeError}`}
        onRetry={() => refresh()}
      />
    );
  }

  if (!data) {
    return <ErrorState message="Couldn't load payment status." onRetry={() => refresh()} />;
  }

  // A demo's Stripe account is a fake its seeding connects: no Stripe session to open, nothing to change (D4, D7).
  if (clientType === "Demo") return <DemoPaymentsCard status={data.status} />;

  // Unknown client type: surface the gap rather than default, so the wrong key never reaches Stripe.
  if (clientType === null && data.status !== "NotStarted") {
    return (
      <Card className={PANEL}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" /> Couldn&apos;t load your payment setup
          </CardTitle>
          <CardDescription>
            We couldn&apos;t tell whether this organisation takes live or test payments. Try signing
            out and back in, or contact support if this persists.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="@container flex flex-col gap-4">
      <SetupSteps status={data.status} />
      {data.status === "NotStarted" ? (
        <NotStartedCard organisationId={organisationId} clientType={clientType} onEnabled={refresh} />
      ) : data.status === "OnboardingInProgress" ? (
        <OnboardingCard organisationId={organisationId} clientType={clientType!} />
      ) : data.status === "Restricted" || data.status === "Rejected" ? (
        <RemediationCard organisationId={organisationId} clientType={clientType!} />
      ) : data.status === "Active" ? (
        <ActiveCard organisationId={organisationId} snapshot={data} clientType={clientType!} />
      ) : null}
    </div>
  );
}

function DemoPaymentsCard({ status }: { status: PaymentAccountSnapshot["status"] }) {
  const active = status === "Active";
  return (
    <Card className={PANEL}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-muted-foreground" />
          Stripe (demo)
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium",
              active ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground",
            )}
          >
            {active ? "Connected" : "Connects when the demo is set up"}
          </span>
        </CardTitle>
        <CardDescription>
          A demo account with made-up card payments. Its payouts reach the demo bank and post to your books, as a real
          Stripe account&apos;s would. It can&apos;t be changed in a demo.
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

/** Restricted and Rejected sit on the verification step, where the missing details are collected. */
function SetupSteps({ status }: { status: PaymentAccountSnapshot["status"] }) {
  const steps = ["Enable payments", "Verify your business", "Accept payments"];
  const current =
    status === "Active" ? steps.length : status === "NotStarted" ? 0 : 1;
  const attention = status === "Restricted" || status === "Rejected";
  return (
    <ol className="grid gap-2 @lg:grid-cols-3">
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li
            key={label}
            className={cn(
              "flex items-center gap-2 rounded-lg border bg-card/60 px-3 py-2 text-xs",
              active && !attention && "border-accent-blue/40 bg-accent-blue/5",
              active && attention && "border-amber-500/40 bg-amber-500/5",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "grid size-5 shrink-0 place-items-center rounded-full border text-[10px] font-semibold tabular-nums",
                done && "border-emerald-500 bg-emerald-500 text-white",
                active && !attention && "border-accent-blue text-accent-blue",
                active && attention && "border-amber-500 text-amber-600 dark:text-amber-400",
                !done && !active && "text-muted-foreground",
              )}
            >
              {done ? <Check className="size-3" /> : i + 1}
            </span>
            <span className={cn(done || active ? "font-medium text-foreground" : "text-muted-foreground")}>
              {label}
            </span>
            <span className="sr-only">{done ? "(done)" : active ? "(current)" : ""}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function EnvironmentBadge({ clientType }: { clientType: ClientType }) {
  const isLive = clientType === "Live";
  return (
    <Badge
      variant="outline"
      className={
        isLive
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
          : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
      }
    >
      {isLive ? "LIVE" : "SANDBOX"}
    </Badge>
  );
}

function RedirectOnboardingAction({
  organisationId,
  disabled,
  disabledReason,
}: {
  organisationId: string;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const [open, setOpen] = useState(false);

  const button = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setOpen(true)}
      disabled={disabled}
    >
      <ExternalLink className="mr-2 h-4 w-4" />
      Continue on Stripe
    </Button>
  );

  return (
    <>
      {disabled && disabledReason ? (
        <TooltipProvider>
          <Tooltip>
            {/* Wrapper span lets the tooltip still trigger on a disabled button. */}
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-block">{button}</span>
            </TooltipTrigger>
            <TooltipContent>{disabledReason}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        button
      )}
      <RedirectOnboardingDialog
        organisationId={organisationId}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}

function NotStartedCard({
  organisationId,
  clientType,
  onEnabled,
}: {
  organisationId: string;
  clientType: ClientType | null;
  onEnabled: () => void;
}) {
  const [enabling, setEnabling] = useState(false);

  const enable = useCallback(async () => {
    setEnabling(true);
    try {
      const response = await authFetch(
        `/api/proxy/v1/organisations/${organisationId}/payment-account/enable`,
        { method: "POST" },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        toast.error(apiErrorMessage(body, `Could not enable payments (status ${response.status}).`));
        return;
      }
      onEnabled();
    } catch (err) {
      toast.error("Could not enable payments", {
        description: err instanceof Error ? err.message : "Network error - please try again.",
      });
    } finally {
      setEnabling(false);
    }
  }, [organisationId, onEnabled]);

  return (
    <Card className={PANEL}>
      <CardHeader>
        <CardTitle>Accept card payments on your invoices</CardTitle>
        <CardDescription className="flex flex-col gap-2">
          <span>
            Let customers pay instantly by scanning the QR code on every invoice you send.
            Powered by Stripe - funds settle into your nominated bank account.
          </span>
          {clientType && (
            <span>
              {clientType === "Live"
                ? "You're setting up live payments."
                : "You're setting up test payments - no real money moves."}
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Same-day or next-day payouts</li>
          <li>Card, Apple Pay, Google Pay, and local methods out of the box</li>
          <li>No setup fees - a small per-transaction share is shown on checkout</li>
        </ul>
        <div>
          <Button onClick={enable} disabled={enabling}>
            {enabling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enable payments
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function OnboardingCard({ organisationId, clientType }: { organisationId: string; clientType: ClientType }) {
  const { connectInstance, initError } = useStripeConnect(organisationId, clientType);

  return (
    <Card className={PANEL}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          Set up payments
        </CardTitle>
        <CardDescription className="flex flex-col gap-2">
          <span>
            {clientType === "Live"
              ? "You're setting up live payments."
              : "You're setting up test payments - no real money moves."}
          </span>
          <span>
            Verify your business details and connect a bank account. Your progress is saved - you
            can return any time.
          </span>
        </CardDescription>
        <CardAction>
          <RedirectOnboardingAction organisationId={organisationId} />
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {initError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {initError}
          </div>
        )}
        {connectInstance ? (
          <ConnectComponentsProvider connectInstance={connectInstance}>
            <ConnectAccountOnboarding
              onExit={() => {
                // The webhook's SignalR push flips the card; no polling needed.
              }}
            />
          </ConnectComponentsProvider>
        ) : !initError ? (
          <div className="flex items-center justify-center p-8">
            <Spinner />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function RemediationCard({ organisationId, clientType }: { organisationId: string; clientType: ClientType }) {
  const { connectInstance, initError } = useStripeConnect(organisationId, clientType);

  return (
    <Card className={PANEL}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-amber-500" />
          Action required
        </CardTitle>
        <CardDescription className="flex flex-col gap-2">
          <span>
            {clientType === "Live" ? "Resuming live payments setup." : "Resuming test payments setup."}
          </span>
          <span>
            We need a few more details before you can accept payments. Update the flagged fields
            below to resume.
          </span>
        </CardDescription>
        <CardAction>
          <RedirectOnboardingAction organisationId={organisationId} />
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {initError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {initError}
          </div>
        )}
        {connectInstance ? (
          <ConnectComponentsProvider connectInstance={connectInstance}>
            <ConnectNotificationBanner
              collectionOptions={{ fields: "currently_due", futureRequirements: "include" }}
            />
            <ConnectAccountManagement
              collectionOptions={{ fields: "currently_due", futureRequirements: "include" }}
            />
          </ConnectComponentsProvider>
        ) : !initError ? (
          <div className="flex items-center justify-center p-8">
            <Spinner />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ActiveCard({
  organisationId,
  snapshot,
  clientType,
}: {
  organisationId: string;
  snapshot: PaymentAccountSnapshot;
  clientType: ClientType;
}) {
  const { connectInstance } = useStripeConnect(organisationId, clientType);
  const caps = snapshot.capabilities;
  const accepting = !!caps?.chargesEnabled;
  return (
    <section className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl border bg-muted/50">
            <CreditCard className="size-5 text-muted-foreground" />
          </span>
          <div>
            <div className="font-semibold">Card payments on invoices</div>
            {accepting ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                <span className="size-1.5 rounded-full bg-emerald-500" /> Accepting payments
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                <span className="size-1.5 rounded-full bg-amber-500" /> Not accepting payments yet
              </span>
            )}
          </div>
        </div>
      </div>

      <p className="mt-3 text-sm text-muted-foreground">
        Invoices you send now include a QR code that customers can scan to pay instantly.
      </p>

      {connectInstance && (
        <div className="mt-4">
          <ConnectComponentsProvider connectInstance={connectInstance}>
            <ConnectNotificationBanner
              collectionOptions={{ fields: "eventually_due", futureRequirements: "include" }}
            />
          </ConnectComponentsProvider>
        </div>
      )}

      <dl className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border @xl:grid-cols-4">
        <StatusRow label="Charges" enabled={caps?.chargesEnabled ?? false} />
        <StatusRow label="Payouts" enabled={caps?.payoutsEnabled ?? false} />
        <StatusRow label="Details submitted" enabled={caps?.detailsSubmitted ?? false} />
        <div className="bg-card p-3">
          <dt>
            <Eyebrow>Enabled</Eyebrow>
          </dt>
          <dd className="mt-1 text-sm font-semibold tabular-nums">
            {snapshot.enabledAt ? formatDate(snapshot.enabledAt) : "-"}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function StatusRow({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div className="bg-card p-3">
      <dt>
        <Eyebrow>{label}</Eyebrow>
      </dt>
      <dd
        className={cn(
          "mt-1 inline-flex items-center gap-1.5 text-sm font-semibold",
          enabled ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground",
        )}
      >
        <span aria-hidden className={cn("size-1.5 rounded-full", enabled ? "bg-emerald-500" : "bg-muted-foreground/50")} />
        {enabled ? "Enabled" : "Pending"}
      </dd>
    </div>
  );
}
